'use strict';
/**
 * This web component encapsulates the functionality of the
 * "phoenix-ad-outstream" tag and makes use of the jwplayer library through
 * the player.js module in order to parse and play video tags.
 *
 * The standard flow of events will be:
 *   1) The component is inserted on the page by Phoenix.
 *   2) The phoenix ad server will set vast-url.
 *   3) The reader will scroll to near the component's location on
 *      the page, triggering the instantiation of the player iframe,
 *      including the JWPlayer library.
 *   4) If the web component receives an adImpression event, indicating a
 *      valid vast tag, it will pause, reveal itself, and reset to the
 *      first frame of the ad. We note the vast tag's clickthrough URL,
 *      if any.
 *
 *      If if gets an adError event, we emit an 'outstreamFailed' event
 *      for the benefit of listeners in Phoenix, hide the player, and
 *      call destroy().
 *   5) Outstream element will expand only when the injection point is
 *      below the viewport or when it's visible.
 *   6) Assuming the ad is set up, when the ad is 50% or more visible,
 *      we begin playback.
 *   7) Once the ad is complete, we put up the end card and expose the
 *      CTA (if any) and close button.
 *
 * This component implements 2 instances of the intersection observer:
 *
 * - The first one makes use of the current phoenix intersection observer
 *   implementation that will load the jwplayer lib.
 * - It also contains a custom implementation of it so that the video player
 *   pauses and resumes playback when the video is 50% percent visible.
 *
 * In order for this component to get initialized 2 conditions need to be met
 * 1 - jwplayer scripts needs to be loaded (the "libReady" variable)
 * 2 - vast-valid attribute has been set by the adServerDfp module
 *
 * After initialization, the player will set the following properties:
 * - vast-valid   {Boolean} true if the vast tag is valid
 *
 * These are tag attributes required in order to instantiate the tag:
 * - id:                        tag id
 * - vast-url:                  Vast file url
 *
 * NOTES:
 * - The jwplayer plays the vast video ad as a pre-roll, which means that it
 *   will play the video ad prior to playing a video file. This is the reason
 *   why we need to feed a 2 secs blank video file to the player instance.
 * - Player will start muted and will mute/unmute on hover/blur events.
 * - Player will set a bool value to the attribute "vast-valid" once the
 *   vast tag is validated.
 *
 */

// Utilize '#force-outstream-fallback' in the URL to force the fallback slot to be
// filled instead of rendering an outstream ad.
const forceOutstreamFallback = window.location.hash.indexOf('force-outstream-fallback') !== -1;

// Temporary shim for debugging the issue observed in ADS-2700.
// This can be removed when ADS-2700 is solved.
if (typeof window.outstreamDebugging === 'undefined') {
    window.outstreamDebugging = [];
}

// phoenix implementation of the intersection observer
const pio = require('../../lib/intersectionObserver.js');
// Player class
const Player = require('./player.js');
// Config values
const configValues = require('./config.js');
// JS dictionary with a few static values
let config = configValues.config;
// iframe srcdoc content
const iframeContent = require('./iframe.html');
// Distance from viewport at which we will load the outstream ads
const DISTANCE_TO_VIEWPORT = 1600;

/**
 * Generates iframe and sets srcdoc attribute
 * @param  {String} src html string template
 * @return {HTMLElement} iframe
 */
function generateIframe() {
    let iframe = document.createElement('iframe'),
        isCompliant = !!('srcdoc' in iframe);

    iframe.classList.add('m-ad-outstream--iframe');
    // Although we are no longer using srcdoc due to ADS-2611, IE11 still
    // exhibits issues, probably for the same underlying reason. Until we've
    // firmed up the exact capability we need to test for, we'll leave this
    // check in, although it's not strictly testing the right thing.
    if (!isCompliant) {
        // This happens in a try/catch, so a throw is fine.
        throw 'Insufficient iframe support';
    }

    return iframe;
}

class PhoenixAdOutstream extends HTMLElement {
    /**
     * Defines what properties will be monitored.
     *
     * @return {Array} Array of strings containing name of properties
     * to monitor
     */
    static get observedAttributes() {
        return ['vast-url'];
    }

    /**
     * Detects when any of the attributes returned by the "observedAttributes"
     * getter gets updated, added or deleted. Note that this is a standard
     * method of custom components.
     *
     * @param  {String} attrName Name of the attribute that was updated
     * @param  {String} oldVal Value that was replaced/removed
     * @param  {String} newVal Value that was set/added
     */
    attributeChangedCallback(attrName, oldVal, newVal) {
        if (attrName === 'vast-url' && newVal && newVal.length) {
            if (forceOutstreamFallback || this.gdprApplicable) {
                // Once vast-url is set, we know Phoenix is ready to receive
                // the failure event and activate the (GDPR-safe) fallback.
                this.emitFailureEvent();
                this.destroy();
            } else {
                // set vast urls and initialize the ad unit.
                this.vastUrl = this.getAttribute('vast-url');
                this.initialize();
            }
        }
    }

    /**
     * This is where we set up our properties and init phoenix's intersection
     * observer
     */
    connectedCallback() {
        // If GDPR is applicable, immediately trigger our fallback. Otherwise,
        // set up the Phoenix intersection observer, using the same threshold
        // of near-visibility as e.g. lazy-loaded images
        if (!window.GdprStatus) {
            throw new Error('window.GdprStatus is required');
        }

        // Will be set to true when the adImpressionHandler method fires
        // which means that the preroll ad has loaded.
        this.adLoaded = false;
        // Changes the scope of some methods to be the current PhoenixAdOutstream
        // instance.
        this.boundCallbacks = this.bindCallbacks();
        // the (optional) CTA URL, as parsed from the VAST tag
        this.clickoutUrl = null;
        // the video has completed playback
        this.didEnd = false;
        // the communications interface between the wrapper and the player
        this.interface = this.generateInterface();
        // there has been an error or the ad has been closed
        this.isDestroyed = false;
        // visibility watcher and listeners have been set up
        this.isInitialized = false;
        // show() has been called due to a valid vast tag, activating the unit
        this.isShown = false;
        // we've been notified from the player iframe that the script is ready
        this.libReady = false;
        // at least config.threshold percent of the wrapper is in the viewport
        this.onScreen = false;
        // used to handle a race condition where the unit is in the viewport
        // before the VAST tag has been validated and show() is called
        this.shouldPlayOnShow = false;
        // used to track whether we've received notice of a valid VAST tag
        this.vastValid = false;
        this.volume = null;

        window.GdprStatus.isApplicable().then((isApplicable) => {
            if (isApplicable) {
                // We are not going to set up the PIO observer; thus, we will
                // never trip the event that loads the JWPlayer library. We
                // shouldn't emit a failure yet, because the surrounding Phoenix
                // code may not have set up the listener for the fallback.
                // Instead we'll set a boolean we check when vast-url is set.
                this.gdprApplicable = true;
            } else {
                pio.observe(this, DISTANCE_TO_VIEWPORT);
            }
        });
    }

    /**
     * stores DOM references, add listeners and sets up player instance
     */
    initialize() {
        if (this.isInitialized || this.isDestroyed || !this.libReady || !this.vastUrl) {
            return;
        }

        this.isInitialized = true;
        // storing DOM references
        this.closeButton = this.querySelector('.m-ad-outstream--close-btn');
        this.cta = this.querySelector('.m-ad-outstream--cta');
        // instantiate visibility watcher
        this.visibilityWatcher = new IntersectionObserver(this.boundCallbacks.onVisibilityChange, {
            threshold: config.threshold,
        });
        this.visibilityWatcher.observe(this);
        this.player.setup(
            this.vastUrl,
            this.getAttribute('player-bidding'),
            this.querySelector('.m-ad-outstream--mute-toggle')
        );
        window.outstreamDebugging.push({
            type: 'setup',
            vastUrl: this.vastUrl,
            bidding: this.getAttribute('player-bidding'),
        });

        this.addListeners();
        // force playback -- we want to send the play event regardless of the
        // internal state so that we can try to generate an adImpression and
        // move to the shown state
        this.play(true);
    }

    addListeners() {
        // DOM events
        this.cta.addEventListener('click', this.boundCallbacks.ctaClickHandler);
        this.closeButton.addEventListener('click', this.boundCallbacks.closeClickHandler);

        // player events
        this.player.on('setupError', this.boundCallbacks.playerErrorHandler);
        this.player.on('complete', this.boundCallbacks.playbackEndedHandler);
        this.player.on('adError', this.boundCallbacks.adErrorHandler);
        this.player.on('adImpression', this.boundCallbacks.adImpressionHandler);
        this.player.on('adStarted', this.boundCallbacks.adStartedHandler);
    }

    removeListeners() {
        // player events
        if (this.player && this.player.off) {
            this.player.off('setupError', this.boundCallbacks.playerErrorHandler);
            this.player.off('complete', this.boundCallbacks.playbackEndedHandler);
            this.player.off('adError', this.boundCallbacks.adErrorHandler);
            this.player.off('adImpression', this.boundCallbacks.adImpressionHandler);
            this.player.off('adStarted', this.boundCallbacks.adStartedHandler);
        }

        // DOM
        if (this.closeButton) {
            this.closeButton.removeEventListener('click', this.boundCallbacks.closeClickHandler);
        }
        if (this.cta) {
            this.cta.removeEventListener('click', this.boundCallbacks.ctaClickHandler);
        }
    }

    instantiatePlayer() {
        this.playerContainer = this.querySelector('.m-ad-outstream--player');
        config.scriptSrc = this.hasAttribute('scriptoverride')
            ? configValues.selfhostedScriptUrl
            : configValues.scriptUrl;

        // Generate our iframe and ad it to the document; if something has gone
        // wrong and we can't, trigger our fallback ad. One source for this
        // error would be a browser such as IE11 which doesn't support srcdoc.
        // Note that a srcdoc polyfill is available, but seems to interact
        // poorly with JWPlayer's behavior (perhaps due to non-standard
        // behavior in document.querySelector and .getElementById within the
        // post-polyfilled iframe).
        try {
            this.iframe = generateIframe();
            this.playerContainer.prepend(this.iframe);
            this.iframe.addEventListener('load', () => {
                // Never instantiate twice.
                if (!this.player) {
                    this.player = new Player(this.iframe.contentWindow, config, this.interface);
                }
            });
            this.iframe.contentWindow.document.open();
            this.iframe.contentWindow.document.write(iframeContent);
            this.iframe.contentWindow.document.close();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('Outstream instantiation error', err);
            this.emitFailureEvent();
            this.destroy();
        }
    }

    /**
     * invokes the show method only when the ad has been loaded and if the
     * outstream is onScreen or below the viewport
     */
    attemptToShow() {
        if (this.adLoaded && (this.onScreen || this.getIsBelowViewport())) {
            this.show();
        }
    }

    showHeader() {
        if (this.parentNode) {
            this.parentNode.querySelector('.m-ad-outstream--header').classList.add('is-shown');
        }
    }

    hideHeader() {
        if (this.parentNode) {
            this.parentNode.querySelector('.m-ad-outstream--header').classList.remove('is-shown');
        }
    }

    /**
     * expands custom tag
     */
    show() {
        this.isShown = true;

        this.classList.add('is-shown');
        this.showHeader();

        if (this.shouldPlayOnShow) {
            this.shouldPlayOnShow = undefined;
            this.play();
        }
    }

    /**
     * collapses custom tag
     */
    hide() {
        this.classList.remove('is-shown');
        // we want to hide the endcard elements when collapsing the custom
        // tag because buttons stay in the center of the container while
        // collapsing which looks odd.
        if (this.cta) {
            this.cta.classList.add('is-hidden');
        }

        setTimeout(() => {
            this.hideHeader();
        }, config.containerAnimationDuration);
    }

    /**
     * In order to hide the playback of the empty video file we want to hide
     * the player layer as soon as possible without any animation
     */
    hidePlayer() {
        this.querySelector('.m-ad-outstream--player').style.display = 'none';
    }

    /**
     * Generates a 'protected' object that will be passed to child instances as
     * an interfaces so they can communicate with this web component instance
     * @return {Object}
     */
    generateInterface() {
        return {
            libReady: this.boundCallbacks.libReadyHandler,
            libLoadError: this.boundCallbacks.libLoadError,
        };
    }

    /**
     * Binds context to callbacks
     */
    bindCallbacks() {
        return {
            libReadyHandler: this.libReadyHandler.bind(this),
            libLoadError: this.libLoadError.bind(this),
            onVisibilityChange: this.onVisibilityChange.bind(this),
            adImpressionHandler: this.adImpressionHandler.bind(this),
            adErrorHandler: this.adErrorHandler.bind(this),
            adStartedHandler: this.adStartedHandler.bind(this),
            playbackEndedHandler: this.playbackEndedHandler.bind(this),
            playerErrorHandler: this.playerErrorHandler.bind(this),
            ctaClickHandler: this.ctaClickHandler.bind(this),
            closeClickHandler: this.closeClickHandler.bind(this),
        };
    }

    /**
     * We want to determine if the oustream element is below the viewport so
     * that we can expand the custom element.
     */
    getIsBelowViewport() {
        return this.getBoundingClientRect().top >= window.innerHeight;
    }

    /**
     * Invoked after clicking on the close button
     */
    closeClickHandler() {
        this.destroy();
    }

    /**
     * Invoked after clicking on the CTA text/icon
     */
    ctaClickHandler() {
        if (this.clickoutUrl) {
            window.open(this.clickoutUrl, '_blank');
        }
    }

    /**
     * Invoked when the we can't play the ad; this could occur because
     * we can't load JWPlayer, we can't contact DFP, the VAST tag from
     * DFP is malformed, or (the most likely case) the VAST tag from
     * DFP is empty.
     */
    adErrorHandler(err) {
        this.vastValid = false;
        this.emitFailureEvent();
        this.hide();
        this.destroy();
        window.outstreamDebugging.push({ type: 'error', obj: err });
    }

    /**
     * Finds out if an outstream video should be blocked based on the
     * duration of the video, dimensions of the video or if the ImaSDKData
     * object is unavailable.
     * @param {Object} imaDetail contains outstream ad ImaSDKData data
     * @returns {Promise}
     */
    validateImaSDKDataPromise(imaDetail) {
        return new Promise((resolve, reject) => {
            // We should still show the oustream player if there's no
            // ima SDK data.
            if (!imaDetail) {
                resolve();
            }

            for (let size of config.roadblockSizes) {
                let dimensions = size.split('x');

                if (
                    imaDetail.vastMediaWidth === parseInt(dimensions[0], 10) &&
                    imaDetail.vastMediaHeight === parseInt(dimensions[1], 10)
                ) {
                    reject(size);
                    break;
                }
            }

            if (imaDetail.duration <= config.minAllowedDuration) {
                reject(`duration_is_lte_than_${config.minAllowedDuration}_sec`);
            }

            resolve();
        });
    }

    /**
     * Invoked when if the vast tag is valid
     */
    adImpressionHandler(impression) {
        // impression.ima is a bundle of information from the IMA SDK
        let imaDetail = impression && impression.ima && impression.ima.ad && impression.ima.ad.g;

        this.validateImaSDKDataPromise(imaDetail)
            .then(() => {
                if (!this.vastValid) {
                    this.adLoaded = true;
                    // First time through; use show() to activate the ad, then
                    // immediately pause it again. No "second time through" code,
                    // because we've disabled replay for now.
                    this.player.seek('0');
                    this.attemptToShow();

                    if (!this.onScreen) {
                        // we need to add a slight delay here otherwise the pause call
                        // will get ignored as we recently called the seek method
                        setTimeout(() => {
                            this.pause();
                        });
                    }
                }

                window.outstreamDebugging.push({ type: 'impression', obj: impression });

                if (impression && impression.clickThroughUrl) {
                    this.clickoutUrl = impression.clickThroughUrl;
                } else if (imaDetail && imaDetail.clickThroughUrl) {
                    this.clickoutUrl = imaDetail.clickThroughUrl;
                }
                if (this.clickoutUrl) {
                    this.cta.classList.add('include-cta');
                }
                this.vastValid = true;
            })
            .catch((errMsg) => {
                window.outstreamDebugging.push({ type: `roadblock_${errMsg}`, obj: imaDetail });
                // ADS-2785: fallback ads should be shown when we get a
                // roadblock video size
                this.emitFailureEvent();
                this.destroy();
            });
    }

    /**
     * Invoked if a VPAID ad is instantiated within the player, after
     * adImpression. We must check for this event as well as
     * adImpression, because the VPAID unit we get may autoplay on its
     * own; if it does, we want to force it to pause if it's offscreen.
     */
    adStartedHandler(impression) {
        if (!this.onScreen) {
            this.player.pause();
        }
    }

    /**
     * invoked when the _video file_ playback ends
     */
    playbackEndedHandler() {
        this.player.ended();
        this.didEnd = true;
        this.hidePlayer();
        this.exposeEndcard();
    }

    /**
     * All changes required required to show our endcard
     */
    exposeEndcard() {
        this.cta.classList.add('is-shown');
        this.closeButton.classList.add('is-shown');
    }

    /**
     * Invoked when there's an error while instantiating the player.
     * This method trigger the component destroy() method
     */
    playerErrorHandler() {
        this.emitFailureEvent();
        this.destroy();
    }

    /**
     * Dispatches error event when the jwplayer fails to instantiate.
     * Note that this is a *likely* occurance and doesn't mean that
     * we necessarily need to log anything further; the most common
     * cause will be that we don't get an ad back from DFP and fall
     * back to page injection, which is a routine case.
     */
    emitFailureEvent() {
        this.dispatchEvent(new CustomEvent('outstreamFailed'));
    }

    /**
     * IntersectionObserver callback that will attempt to start playback
     * only if the component is visible, else it will try to pause playback.
     * @param {Array} entries
     */
    onVisibilityChange(entries) {
        if (this.isDestroyed || this.didEnd) {
            return;
        }

        let isVisible = entries[0].intersectionRatio >= config.threshold;

        if (isVisible) {
            this.onContainerVisible();
        } else {
            this.onContainerNotVisible();
        }
    }

    onContainerVisible() {
        this.onScreen = true;

        if (this.isShown) {
            this.play();
        } else {
            this.shouldPlayOnShow = true;
        }

        this.attemptToShow();
    }

    onContainerNotVisible() {
        this.onScreen = false;
        this.pause();
    }

    /**
     * Attempts to start playback
     * @param  {Boolean} force Force playback
     */
    play(force) {
        // this will be used in cases when the player needs to start playing
        // in order to load the pre-roll ad data.
        if (force === true) {
            this.player.play();
            return;
        }
        if (!this.player || this.player.getState() === 'playing') {
            return;
        }
        this.player.play();
    }

    /*
     * Attempts to pause playback
     */
    pause() {
        this.player.pause();
    }

    /**
     * @protected
     * Invoked by child player instance when the jwplayer lib has been loaded
     */
    libReadyHandler() {
        this.libReady = true;
        this.initialize();
    }

    /**
     * @protected
     * invoked by child player instance when an error was thrown while
     * loading the jwplayer instance
     */
    libLoadError(err) {
        // eslint-disable-next-line no-console
        console.warn(err);
        this.emitFailureEvent();
        this.destroy();
    }

    /**
     * This is the callback for phoenix's intersection observer.
     * It will get invoked when the visible portion of the viewport
     * is 800px or less above or below the container slot.
     */
    onObserved() {
        this.instantiatePlayer();
    }

    get vastValid() {
        return this.getAttribute('vast-valid') === 'true';
    }

    set vastValid(val) {
        this.setAttribute('vast-valid', !!val);
    }

    destroy() {
        if (this.isDestroyed) {
            return;
        }

        let duration = this.isShown ? config.containerAnimationDuration : 0;

        this.isDestroyed = true;
        this.hide();

        // wait until the animation has completed before destroying the
        // instance
        setTimeout(() => {
            this.removeListeners();
            if (this.visibilityWatcher) {
                this.visibilityWatcher.unobserve(this);
                this.visibilityWatcher.disconnect(this);
            }

            if (this.cta) {
                this.cta.classList.remove('is-shown');
            }
            if (this.closeButton) {
                this.closeButton.classList.remove('is-shown');
            }

            // It's possible that this will fail, as if we haven't succeeded in
            // instantiating the player. We will swallow this edge case.
            try {
                this.player.destroy();
            } catch (err) {}

            this.visibilityWatcher = null;
            this.boundCallbacks = null;
            this.interface = null;
            this.player = null;
            this.closeButton = null;
            this.cta = null;
        }, duration);
    }
}

// register component
window.customElements.define('phoenix-ad-outstream', PhoenixAdOutstream);
